<template>
  <div id="repository-curricula" class="w-full">
    <div id="curricula-header" class="flex items-center justify-between gap-10 px-6 py-4 ">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <BlTextTitle3 text="Curricula" class="text-dark-purple-100" />
          <BlBubbleCount :count="totalCurriculums || 0" />
        </div>
        <BlTextBody2 text="Create curricula to classify and manage the targets." />
      </div>
      <BlInputButton v-if="currentUser.role.includes('admin')" icon="plus" :label="'New curriculum'"
        @clicked="newOpened = true" />
    </div>
    <SuperAdminNewCurriculumModal v-if="newOpened" @close-modal="newOpened = false" />
    <div v-if="!currentUser.role.includes('admin')" class="w-full px-6">
      <div class="flex items-center gap-2 px-4 py-2 rounded-b bg-info-100">
        <div class="grid flex-shrink-0 w-10 h-10 place-items-center"
          :style="{ background: '#E4F1FA', borderRadius: '0.25rem' }">
          <BlIcon icon="toast-info" class="w-6 h-6" />
        </div>
        <BlTextBody1 class="text-info"
          text="Please note that adding or editing the curricula is restricted to the admin role." />
      </div>
    </div>
    <div class="flex items-center gap-2 px-6 py-4 top-18 bg-slate-100">
      <BlInputSearch class="w-80" :input="searchInput" @changed="onInputSearch" />
      <BlSort class="w-60" v-model="sortInput" :options="sortOptions"
        :defaultValue="{ primary: 'date', secondary: 'desc' }" @change="onChangeSort($event)" />
    </div>
    <div v-if="pageLoading" class="grid h-128 place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div v-else-if="!curriculums.length" class="flex flex-col items-center justify-center h-128">
      <BlTextBody1 class="text-center" text="Currently, there are no curricula to display." />
      <BlTextBody1 class="text-center" text="Once you create a new curriculum, it will appear here." />
      <BlInputButton label="Create a new curriculum" class="p-0" type="plain" @clicked="newOpened = true" />
    </div>
    <div v-else class="flex flex-col gap-6 px-6 pb-10">
      <div class="border rounded border-slate-400">
        <div class="sticky z-10 flex items-center flex-grow-0 w-full bg-slate-300" :style="{ top: '70px' }">
          <div class="flex items-center flex-shrink-0 h-12 px-4 rounded-tl" :style="{ width: '25%' }">
            <BlTextSubtitle3 text="Name" />
          </div>
          <div class="flex items-center flex-shrink-0 h-12 px-4" :style="{ width: '12.5%' }">
            <BlTextSubtitle3 text="Number of targets" />
          </div>
          <div class="flex items-center flex-shrink-0 h-12 px-4" :style="{ width: '50%' }">
            <BlTextSubtitle3 text="Description" />
          </div>
          <div class="flex items-center flex-shrink-0 h-12 px-4 rounded-tr" :style="{ width: '12.5%' }">
            <BlTextSubtitle3 text="" />
          </div>
        </div>
        <div v-for="curriculum in curriculums" :key="curriculum.id"
          class="flex items-center flex-grow-0 w-full border-b border-slate-400 bg-pure-white">
          <div class="flex flex-shrink-0 h-20 gap-2 px-4 py-3" :style="{ width: '25%' }" :title="curriculum.name">
            <div class="flex-shrink-0 w-4 h-4 mt-1 rounded-full" :style="{ backgroundColor: curriculum.color }" />
            <router-link :to="`/super-admin/databank/${$route.params.id}/curriculum/${curriculum.id}`
              ">
              <BlTextBody1 class="underline line-clamp-2 text-slate-1000 h-13" :text="curriculum.name" />
            </router-link>
          </div>
          <div class="flex-shrink-0 h-20 px-4 py-3" :style="{ width: '12.5%' }">
            <BlTextBody1 class="line-clamp-2 h-13" :text="curriculum.number_of_targets" />
          </div>
          <div class="flex-shrink-0 h-20 px-4 py-3" :style="{ width: '50%' }" :title="curriculum.description">
            <BlTextBody1 class="whitespace-pre-line line-clamp-2 h-13" :text="curriculum.description" />
          </div>
          <div class="flex justify-end flex-shrink-0 h-20 gap-2 px-4 py-3" :style="{ width: '12.5%' }">
            <BlInputButton v-if="currentUser.role.includes('admin')" label="Edit" type="outlined" dense
              @clicked="editOpened = curriculum.id" />
          </div>
        </div>
      </div>
      <BlInputPagination v-if="totalCurriculums > 25" :page="page" :perPage="perPage" :total="totalCurriculums"
        @changed="changePage" @changed-per-page="changePerPage" />
    </div>
    <EditCurriculumModal v-if="editOpened" :curriculum="curriculums.find(i => i.id === editOpened)"
      @close-modal="editOpened = null" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";

import SuperAdminNewCurriculumModal from "./NewCurriculumModal.vue";
import EditCurriculumModal from "../../../repository/curricula/EditCurriculumModal.vue";

import BlBubbleCount from "../../../BlBubbleCount.vue";
import BlInputButton from "../../../BlInputButton.vue";
import BlTextBody2 from "../../../BlTextBody2.vue";
import BlTextTitle3 from "../../../BlTextTitle3.vue";
import BlTextBody1 from "../../../BlTextBody1.vue";
import BlInputSearch from "../../../BlInputSearch.vue";
import BlTextSubtitle3 from "../../../BlTextSubtitle3.vue";
import BlInputPagination from "../../../BlInputPagination.vue";
import BlIcon from "../../../BlIcon.vue";
import BlSort from "../../../BlSort.vue";

export default {
  name: "SuperAdminCurricula",
  components: {
    SuperAdminNewCurriculumModal,
    EditCurriculumModal,
    BlTextTitle3,
    BlBubbleCount,
    BlInputButton,
    BlTextBody2,
    BlTextBody1,
    BlInputSearch,
    BlTextSubtitle3,
    BlInputPagination,
    BlIcon,
    BlSort
  },
  data() {
    return {
      newOpened: false,
      editOpened: null,
      pageLoading: true,
      page: 1,
      perPage: 25,
      searchInput: {
        placeholder: "Search curriculum by name",
        model: ""
      },
      sortInput: { primary: "date", secondary: "desc" },
      sortOptions: {
        primary: [
          { value: "name", label: "Name" },
          { value: "date", label: "Date created" }
        ],
        secondary: [
          { value: "asc", label: "Ascending" },
          { value: "desc", label: "Descending" }
        ]
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "curriculums", "totalCurriculums"]),
    getParams() {
      let params = `?page=${this.page}&per_page=${this.perPage}`;
      const query = this.searchInput.model;
      const sort = this.sortInput;
      if (query) params += `&query=${query}`;
      // - sort mapping -
      // name, ascending -> name_asc
      // name, descending -> name_desc
      // date created, ascending -> oldest
      // date created, descending -> newest
      if (sort.primary === "date") {
        params += `&sort=${sort.secondary === "desc" ? "newest" : "oldest"}`;
      } else {
        params += `&sort=${sort.primary}_${sort.secondary}`;
      }
      return params;
    }
  },
  mounted() {
    this.pageLoading = true;
    const searchParams = this.$route.query;
    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["per_page"]) {
      this.perPage = Number(searchParams["per_page"]);
    }
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["query"];
    }
    if (searchParams["sort"]) {
      const temp = searchParams["sort"] || "";
      // prettier-ignore
      const sorts = {
        name_asc: { primary: "name", secondary: "asc" },
        name_desc: { primary: "name", secondary: "desc" },
        oldest: { primary: "date", secondary: "asc" },
        newest: { primary: "date", secondary: "desc" }
      };
      if (temp && sorts[temp]) {
        this.sortInput = sorts[temp];
      }
    }

    this.fetchCurriculums();
  },
  methods: {
    ...mapActions(["getCurriculums"]),
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchCurriculums();
    }, 1000),
    onChangeSort(val) {
      const { primary, secondary } = this.sortInput;
      if (val.primary !== primary || val.secondary !== secondary) {
        this.sortInput = val;
        this.page = 1;
        this.fetchCurriculums();
      }
    },
    changePage(page) {
      if (page === this.page) return;
      this.page = page;
      this.fetchCurriculums();
    },
    changePerPage(perPage) {
      if (perPage === this.perPage) return;
      this.page = 1;
      this.perPage = perPage;
      this.fetchCurriculums();
    },
    fetchCurriculums() {
      this.pageLoading = true;
      this.getCurriculums({ params: `${this.getParams}&databank_id=${this.$route.params.id}` }).then(() => {
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = `/super-admin/databank/${this.$route.params.id}/curriculum${this.getParams}`;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/superAdmin/databank/curricula/Curricula.vue